<template>
  <div class="colorspace elevatedStage inset max30">
    <div class="tabs-header">
      <div :class="['tab-item', { active: activeTab === 'svg' }]" @click="activeTab = 'svg'" title="SVG Properties">
        <i class="fas fa-circle-info"></i>
      </div>
      <div :class="['tab-item', { active: activeTab === 'palette' }]" @click="activeTab = 'palette'" title="Color Palette">
        <i class="fas fa-swatchbook"></i>
      </div>
      <div :class="['tab-item', { active: activeTab === 'element' }]" @click="switchToElementTab" title="Selected Element">
        <i class="fas fa-mouse-pointer"></i>
      </div>
    </div>

    <!-- SVG PROPERTIES TAB -->
    <div v-if="activeTab === 'svg'" class="tab-content">
      <div class="fullWidth" style="margin-bottom: 20px; border-bottom: 1px solid var(--border-color); padding-bottom: 10px;">
        <div class="leftAlign heading txt">SVG Properties</div>
      </div>
      <div class="property-list no-separator">
        <div class="property-item vertical">
          <span class="label">Name</span>
          <input type="text" class="custom-input" v-model="svgId" @change="updateSvgId" placeholder="svg-id" />
        </div>
        
        <div class="dimension-group">
          <div class="property-item no-border">
            <span class="label">Width</span>
            <el-input-number v-model="svgWidth" @change="onWidthChange" size="default" :min="1"></el-input-number>
          </div>

          <div class="property-item no-border">
            <span class="label">Height</span>
            <el-input-number v-model="svgHeight" @change="onHeightChange" size="default" :min="1"></el-input-number>
          </div>
        </div>
      </div>
      <div class="tip-box" style="margin-top: 20px;">
        <span>Updating these values will directly modify the SVG attributes.</span>
      </div>
    </div>

    <!-- COLOR PALETTE TAB -->
    <div v-if="activeTab === 'palette'" class="tab-content">
      <div class="fullWidth" style="margin-bottom: 20px; border-bottom: 1px solid var(--border-color); padding-bottom: 10px;">
        <div class="leftAlign heading txt">Color Palette</div>
      </div>

      <div class="fullWidth" style="margin:10px 0px 10px 10px">
        <div class="leftAlign subheading txt">Current</div>
        <el-button class="bottomOptionsRightItem btntxt" type="text" size="medium" @click="resetColors">Reset</el-button>
      </div>

      <div>
        <ColorPickerListHolder class="emptyholder" 
          :colors="curColors" 
          v-on:on-user-select-color-change="$emit('on-user-select-color-change', $event)"
          v-on:on-user-hover-on-color="$emit('on-user-hover-on-color', $event)"/> 
      </div>

      <div class="fullWidth" style="margin:20px 0px 10px 10px">
        <div class="leftAlign subheading txt">Suggestions</div>
        <el-button class="bottomOptionsRightItem" :icon="Refresh" size="mini" circle @click="populatePallette"></el-button>
      </div>

      <div v-if='curColors.length>1' class="fullWidth" style="margin-bottom:5px">
        <el-button-group class="rightAlign" style="margin-top:2px">
          <el-button :class="{ btn: isPrimary('mono') }" :type="isPrimary('mono')" size="mini" @click="type='mono'">Mono</el-button>
          <el-button :class="{ btn: isPrimary('compli') }" :type="isPrimary('compli')" size="mini" @click="type='compli'">Duo</el-button>
          <el-button :class="{ btn: isPrimary('triad') }" v-if='curColors.length>2' :type="isPrimary('triad')" size="mini" @click="type='triad'">Triad</el-button>
          <el-button :class="{ btn: isPrimary('random') }" :type="isPrimary('random')" size="mini" @click="type='random'">I'm lucky</el-button>
        </el-button-group>
      </div>
      
      <div style="margin:10px">
        <ColorSliders :count='getSlidersCount()' v-on:on-user-select-hues-change="selectHues($event)" />
      </div>

      <div id="suggestions">
        <ColorListHolder class="holder" v-for="(sug,index) in suggestionList" :key="sug" :colors="sug" size="40" @click="selectSuggestion(index)"/> 
      </div>
    </div>
    
    <!-- SELECTED ELEMENT TAB -->
    <div v-if="activeTab === 'element'" class="tab-content">
      <div class="fullWidth" style="margin-bottom: 20px; border-bottom: 1px solid var(--border-color); padding-bottom: 10px;">
        <div class="leftAlign heading txt">Selected Element</div>
      </div>

      <div v-if="selectedElementColor">
        <div class="fullWidth" style="margin:10px 0px 10px 10px">
          <div class="leftAlign subheading txt">Translate</div>
          <el-button class="bottomOptionsRightItem btntxt" type="text" size="medium" @click="$emit('on-element-unselected')">Deselect</el-button>
        </div>
        <div style="margin:20px 10px">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <i class="fas fa-arrows-alt-h" title="Horizontal Movement" style="width: 20px; opacity: 0.6;"></i>
            <input type="range" min="-500" max="500" v-model="translateX" @input="updateElementTranslate" style="flex: 1"/>
          </div>
          <div style="display: flex; align-items: center; gap: 10px;">
            <i class="fas fa-arrows-alt-v" title="Vertical Movement" style="width: 20px; opacity: 0.6;"></i>
            <input type="range" min="-500" max="500" v-model="translateY" @input="updateElementTranslate" style="flex: 1"/>
          </div>
        </div>
        
        <div class="fullWidth" style="margin:10px 0px 10px 10px">
          <div class="leftAlign subheading txt">Scale & Rotate</div>
        </div>
        <div style="margin:20px 10px">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <i class="fas fa-expand-arrows-alt" title="Size" style="width: 20px; opacity: 0.6;"></i>
            <input type="range" min="10" max="500" v-model="elemScale" @input="updateElementScale" style="flex: 1"/>
          </div>
          <div style="display: flex; align-items: center; gap: 10px;">
            <i class="fas fa-sync-alt" title="Rotate" style="width: 20px; opacity: 0.6;"></i>
            <input type="range" min="-180" max="180" v-model="elemRotate" @input="updateElementRotate" style="flex: 1"/>
          </div>
        </div>

        <div class="fullWidth" style="margin:10px 0px 10px 10px">
          <div class="leftAlign subheading txt">Color</div>
        </div>
        <div style="margin:20px 10px">
          <div style="display: flex; width: 100%; height: 50px; border-radius: 8px; border: 3px solid var(--border-color); overflow: hidden; margin-bottom: 20px; box-shadow: 4px 4px 0px var(--border-color);">
             <div :style="{width: '50%', height: '100%', background: originalElementColor}"></div>
             <div :style="{width: '50%', height: '100%', background: previewColor}"></div>
          </div>
          
          <div style="margin-bottom: 15px; display: flex; align-items: center;">
            <span style="font-weight: light; margin-right: 10px;">Hex:</span>
            <input type="text" class="custom-input" v-model="hexInput" @input="updateColorFromHex" style="width: 100px;" />
          </div>

          <div style="text-align: left; margin-top: 10px; font-weight: light;">Hue</div>
          <input type="range" min="0" max="360" v-model="elemH" @input="updateElementColor" style="width:100%"/>
          <div style="text-align: left; margin-top: 10px; font-weight: light;">Saturation</div>
          <input type="range" min="0" max="100" v-model="elemS" @input="updateElementColor" style="width:100%"/>
          <div style="text-align: left; margin-top: 10px; font-weight: light;">Value (Lightness)</div>
          <input type="range" min="0" max="100" v-model="elemL" @input="updateElementColor" style="width:100%"/>
        </div>
        <div class="tip-box">
          <span><b>Tip:</b> 'Ctrl + Click' can be used to select multiple elements and edit them together.</span>
        </div>
      </div>
      <div v-else class="empty-state">
        <el-icon :size="48" style="margin-bottom: 16px; opacity: 0.3;"><pointer /></el-icon>
        <p>No element selected. Click on an element in the workspace to edit its properties.</p>
      </div>
    </div>

  </div>
</template>

<script>
import ColorListHolder from './ColorListHolder.vue'
import ColorPickerListHolder from './ColorPickerListHolder.vue'
import { getRandomPallette, getPalette, getPaletteFromGivenHues } from '../js/colgen.js' // eslint-disable-line no-unused-vars
import ColorSliders from './ColorSliders.vue'
import { Color } from '@svgdotjs/svg.js'
import { Refresh, InfoFilled} from '@element-plus/icons-vue'

export default {
  name: 'Colorspace',
  components: {
    ColorListHolder, ColorPickerListHolder, ColorSliders
  },
  setup() {
    return {
      Refresh,
      InfoFilled
    }
  },
  props: { 
    curColors: Array,
    refreshPalette: Boolean,
    selectedElementColor: String
  },
  data() {
    return {
      activeTab: 'palette',
      svgId: '',
      svgWidth: 0,
      svgHeight: 0,
      suggestionList: [],
      type: 'mono',
      elemH: 0,
      elemS: 0,
      elemL: 0,
      translateX: 0,
      translateY: 0,
      elemScale: 100,
      elemRotate: 0,
      previewColor: '#000000',
      originalElementColor: '#000000',
      hexInput: '#000000',
      aspectRatio: 1
    }
  },
  mounted(){
    this.populatePallette();
    this.updateSvgProps();
  },
  watch:{
    activeTab(newVal, oldVal) {
      // If we switch away from the element tab, notify parent to unselect the element
      if (oldVal === 'element' && newVal !== 'element') {
        this.$emit('on-element-unselected');
      }
    },
    type(oldVal, newVal){ // eslint-disable-line no-unused-vars
      this.populatePallette();
    },
    refreshPalette(oldVal, newVal){ // eslint-disable-line no-unused-vars
      this.populatePallette();
    },
    // Update SVG properties whenever the colors change (indicates a new SVG or recolor)
    curColors() {
      this.updateSvgProps();
      this.populatePallette();
    },
    selectedElementColor(newVal) {
      if (newVal) {
        let c = new Color(newVal).hsl();
        this.elemH = c.h || 0;
        this.elemS = c.s || 0;
        this.elemL = c.l || 0;
        this.previewColor = newVal;
        this.originalElementColor = newVal;
        this.hexInput = newVal;
        this.translateX = 0;
        this.translateY = 0;
        this.elemScale = 100;
        this.elemRotate = 0;
        this.activeTab = 'element';
      }
    }
  },
  methods: {
    updateSvgProps() {
      // Use nextTick to ensure the DOM is updated before reading SVG attributes
      this.$nextTick(() => {
        const svg = document.getElementById("svgHolder")?.querySelector('svg');
        if (svg) {
          this.svgId = svg.id;
          this.svgWidth = parseFloat(svg.getAttribute('width')) || svg.viewBox.baseVal.width;
          this.svgHeight = parseFloat(svg.getAttribute('height')) || svg.viewBox.baseVal.height;
          this.aspectRatio = this.svgWidth / this.svgHeight;
        }
      });
    },
    onWidthChange(val) {
      this.svgHeight = Math.round(val / this.aspectRatio);
      this.updateSvgDimensions();
    },
    onHeightChange(val) {
      this.svgWidth = Math.round(val * this.aspectRatio);
      this.updateSvgDimensions();
    },
    updateSvgId() {
      const svg = document.getElementById("svgHolder")?.querySelector('svg');
      if (svg) {
        svg.id = this.svgId;
      }
    },
    updateSvgDimensions() {
      const svg = document.getElementById("svgHolder")?.querySelector('svg');
      if (svg) {
        svg.setAttribute('width', this.svgWidth);
        svg.setAttribute('height', this.svgHeight);
      }
    },
    switchToElementTab() {
      this.activeTab = 'element';
      if (!this.selectedElementColor) {
        // Find first valid interactive element and trigger its click
        const svg = document.getElementById("svgHolder")?.querySelector('svg');
        if (svg) {
          const firstElem = svg.querySelector('path, rect, circle, ellipse, polygon, polyline');
          if (firstElem) {
            firstElem.dispatchEvent(new MouseEvent('click', { bubbles: true }));
          }
        }
      }
    },
    isPrimary(val){
      if(this.type == val){
        return 'primary'
      }else{
        return ''
      }
    },
    getSlidersCount(){
      switch(this.type){
        case 'mono':
          return 1;
        case 'compli':
          return 2;
        case 'triad':
          return 3;
        default:
          return 0;
      }
    },
    populatePallette(){
      this.suggestionList = [];
      for (let step = 0; step < 5; step++) {
          var colors = getPalette(Array.from(this.curColors), this.type);
          this.suggestionList.push(Array.from(colors));
      }
    },
    selectSuggestion(index){
      this.$emit('on-user-select-color-change', this.suggestionList[index]);
    },
    selectHues(hues){
      this.$emit('on-user-select-color-change', getPaletteFromGivenHues(Array.from(this.curColors), Array.from(hues)));
    },
    resetColors(){
      this.$emit('on-reset-colors');
    },
    updateElementColor() {
      let c = new Color({h: parseFloat(this.elemH), s: parseFloat(this.elemS), l: parseFloat(this.elemL)});
      let hex = c.toHex();
      this.previewColor = hex;
      this.hexInput = hex;
      this.$emit('on-element-color-change', hex);
    },
    updateColorFromHex() {
      if (/^#[0-9A-F]{6}$/i.test(this.hexInput) || /^#[0-9A-F]{3}$/i.test(this.hexInput)) {
        let c = new Color(this.hexInput).hsl();
        this.elemH = c.h || 0;
        this.elemS = c.s || 0;
        this.elemL = c.l || 0;
        this.previewColor = this.hexInput;
        this.$emit('on-element-color-change', this.hexInput);
      }
    },
    updateElementTranslate() {
      this.$emit('on-element-translate', { x: parseFloat(this.translateX), y: parseFloat(this.translateY) });
    },
    updateElementScale() {
      this.$emit('on-element-scale', parseFloat(this.elemScale) / 100);
    },
    updateElementRotate() {
      this.$emit('on-element-rotate', parseFloat(this.elemRotate));
    }
  }
}
</script>

<style>
.tabs-header {
  display: flex;
  background-color: var(--bg-color);
  border-bottom: 1px solid var(--border-color);
  margin: -12px -12px 20px -12px;
  border-radius: 12px 12px 0 0;
}

.tab-item {
  flex: 1;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: var(--text-dark);
  opacity: 0.5;
  transition: all 0.2s ease;
}

.tab-item:hover {
  opacity: 1;
  background-color: rgba(0,0,0,0.02);
}

html.dark-theme .tab-item:hover {
  background-color: rgba(255,255,255,0.05);
}

.tab-item.active {
  opacity: 1;
  color: var(--primary-color);
  border-bottom: 2px solid var(--primary-color);
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

.property-list {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.property-list.no-separator .property-item {
  border-bottom: none;
}

.property-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  width: 100%;
}

.property-item.vertical {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.property-item.no-border {
  border: none;
  padding: 4px 0;
}

.dimension-group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
}

.property-item.no-border {
  border: none;
  padding: 4px 0;
  width: 100%;
}

.property-item .label {
  font-weight: bold;
  color: var(--text-dark);
}

.empty-state {
  padding: 40px 20px;
  opacity: 0.7;
}

.elevatedStage.colorspace:hover {
  transform: none;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.max30{
  width: 100%;
}

.mainHeading{
  font-size: large;
  font-weight: bold;
}

.inset{
  padding: 12px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

@media screen and (max-width: 800px) {
  .inset{
    padding: 12px;
    position: relative;
    z-index: 0;
    top:0px;
    height: auto;
  }
}

.leftAlign{
  float: left; 
}

.fullWidth{
  width: 100%; display: inline-block;
}

.holder{
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  border: whitesmoke 0px solid;
  margin-bottom: 4px;
}

.holder:hover{
  background-color: var(--border-color);
}

.bottomOptionsRightItem{
  float:  right;
  margin-right: 16px;
}

.tip-box {
  margin-top: 30px;
  padding: 10px;
  background-color: var(--bg-color);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  font-size: medium;
  text-align: left;
  display: flex;
  align-items: center;
}

.custom-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--text-light);
  color: var(--text-dark);
  font-family: inherit;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.custom-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

html.dark-theme .custom-input {
  background-color: #1e293b;
  border-color: var(--border-color);
}

</style>
